import { useState, useEffect } from "react";
import { timeAgo } from "../../../app/datetimeUtils";
import { NoTitleEntered } from "../../../app/draftHelper";
import { integerName } from "../../../app/utils";
import { DataTablesViewModel } from "../../../interfaces/dataTables";
import PromptPanel from "../../prompts/PromptPanel";
import { StimulusMaterialViewModel } from "../../stimulusMaterial/stimulusMaterialApiSlice";
import {
	CoursePromptModel,
	DraftStatus,
	WritingAction,
	WritingHistoryQueryModel,
	WritingHistoryViewModel,
	useLazyWritingHistoryQuery,
} from "../writingApiSlice";
import { DraftActionButton } from "./DraftActionButton";
import { PromptStatus } from "./StudentWriting";
import { WritingTableData } from "./WritingTable";

export interface PromptPanelPrompt {
	id: number;
	name: string;
	renderedText: string;
	signLanguageVideoURL: string;
	genre: string;
	stimulusMaterials: StimulusMaterialViewModel[];
}

export interface PromptPanelData {
	coursePrompt: CoursePromptModel;
	id: number;
	name: string;
	genre: string;
	wasPracticedBefore: boolean;
}

interface PromptPanelProps {
	className?: string;
	data: WritingTableData;
}

export function PromptWritingPanel({ data }: PromptPanelProps) {
	const { coursePrompt, writing } = data.row;
	const { prompt } = coursePrompt;

	const [writingHistory, setWritingHistory] = useState<
		DataTablesViewModel<WritingHistoryViewModel> | null | undefined
	>();

	const [getWritingHistory, result] = useLazyWritingHistoryQuery();

	const newEssayButtonClassName =
		data.status === "Not Started" ? "btn-primary" : "btn-action btn-small";

	useEffect(() => {
		setWritingHistory(undefined);
		if (writing) {
			const args: WritingHistoryQueryModel = {
				promptFilter: prompt.promptId.toString(),
				iDisplayStart: 0,
				iDisplayLength: 10,
				iSortCol_0: 0,
				sSortDir_0: "desc",
			};
			getWritingHistory(args)
				.unwrap()
				.then((data) => setWritingHistory(data));
		} else {
			setWritingHistory(null);
		}
	}, [data]);

	return (
		<div className="-mx-4 -my-3 p-4 bg-gray-100 lg:flex lg:justify-between">
			<PromptPanel
				name={prompt.name}
				showName={true}
				renderedText={prompt.renderedText}
				stimulusMaterials={prompt.stimulusMaterials}
				showGenre={true}
				genre={prompt.genre}
				signLanguageVideoURL={prompt.signLanguageVideoURL}></PromptPanel>
			<div className="lg:basis-0 lg:grow lg:ml-4">
				<section className="mt-4 lg:mt-0">
					<h3 className="font-semibold">Your Writing History</h3>
					{!writing ? (
						<>
							<OverdueMessage coursePrompt={coursePrompt} />
							<p>You have not written any essays for the prompt “{prompt.name}.”</p>
						</>
					) : (
						<>
							<OverdueMessage coursePrompt={coursePrompt} />

							<p>
								{writing.essayCount > 0 &&
									`You have started
								${integerName(writing.essayCount)} 
							    ${writing.essayCount == 1 ? "essay" : "essays"}
								for this prompt.`}
							</p>

							{writingHistory === undefined && result.isFetching && <p>Loading...</p>}
							{writingHistory && (
								<ul>
									{writingHistory.aaData.map((row, index) => (
										<WritingHistoryCard
											key={row.essayId}
											model={row}
											index={index}
										/>
									))}
								</ul>
							)}
						</>
					)}
				</section>

				<div className="mt-4">
					<h3 className="font-medium">Start a New Essay</h3>

					<div className="mt-2 flex space-x-2">
						{(coursePrompt.allowPrewriting || coursePrompt.isOrganizerRequired) && (
							<DraftActionButton
								id={`start-new-organizer-${data.promptId}`}
								action={WritingAction.StartOrganizing}
								actionId={prompt.promptId}
								className={newEssayButtonClassName}
								disabled={data.status === PromptStatus.Expired}
							/>
						)}
						{!coursePrompt.isOrganizerRequired && (
							<DraftActionButton
								id={`start-new-essay-${data.promptId}`}
								action={WritingAction.StartWriting}
								actionId={prompt.promptId}
								className={newEssayButtonClassName}
								disabled={data.status === PromptStatus.Expired}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

interface WritingHistoryCardProps {
	model: WritingHistoryViewModel;
	index: number;
	handleReviseEssay?: (model: WritingHistoryViewModel) => void;
}

function WritingHistoryCard(props: WritingHistoryCardProps) {
	const model = props.model;
	const titleClassName = model.title === NoTitleEntered ? "italic" : "";

	const statusDescription = () => {
		const updateDate = timeAgo(model.lastDraftUpdateDate).toLowerCase();

		if (model.action == WritingAction.Organize) {
			return `You were organizing this essay ${updateDate}.`;
		}

		switch (model.lastDraftStatus) {
			case DraftStatus.Incomplete:
				return `You were working on a draft ${updateDate}.`;
			case DraftStatus.Scored:
				return `You submitted this essay for scoring ${updateDate}.`;
			case DraftStatus.Finalized:
				return `You finalized your essay ${updateDate}.`;
			case DraftStatus.ScoringFailed:
				return `Your essay could not be scored ${updateDate}.`;
		}

		return "Your essay cannot be worked on any further.";
	};

	return (
		<li className="border border-gray-300 bg-gray-50 p-4 my-2 rounded-md">
			<div className="flex justify-between">
				<div className="mr-4">
					<p>
						Title: <span className={titleClassName}>{model.title}</span>
					</p>
					<p>{statusDescription()}</p>
					<p>
						{`You started this essay ${timeAgo(model.essayCreateDate).toLowerCase()}.`}
					</p>
				</div>
				<div className="flex flex-col space-y-4 self-center">
					{model.action && (
						<DraftActionButton
							id={`${model.action == WritingAction.ScoreReport ? "view-report" : "continue"}-${model.essayId}`}
							action={model.action}
							actionId={model.actionDraftId}
							className={`${props.index === 0 ? "btn-primary" : "btn-action btn-small"}`}
						/>
					)}
					{model.action === WritingAction.ScoreReport &&
						!(model.dueDateRequired && model.dueDateExceeded) &&
						model.remainingRevisions > 0 &&
						model.lastDraftStatus === "Scored" && (
							<DraftActionButton
								id={`revise-${model.essayId}`}
								action={WritingAction.ReviseEssay}
								actionId={model.actionDraftId}
								className={`${props.index === 0 ? "btn-primary" : "btn-action btn-small"}`}
							/>
						)}
				</div>
			</div>
		</li>
	);
}

function OverdueMessage(props: { coursePrompt: CoursePromptModel }) {
	if (!props.coursePrompt.dueDateUtc) return null;
	if (!props.coursePrompt.isOverdue) return null;

	const dueDate = timeAgo(props.coursePrompt.dueDateUtc).toLowerCase();
	if (props.coursePrompt.isDueDateRequired) {
		return (
			<p className="text-red-600">{`This assignment was due ${dueDate}, and you can no longer work on it.`}</p>
		);
	} else {
		return (
			<p className="text-amber-800">{`This assignment was due ${dueDate}, but you may still work on it.`}</p>
		);
	}
}
